import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';
import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';

/**
 * A custom element that displays a cart icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount'];

  /** @type {number} */
  get currentCartCount() {
    return parseInt(this.refs.cartBubbleCount.textContent ?? '0', 10);
  }

  set currentCartCount(value) {
    this.refs.cartBubbleCount.textContent = value < 100 ? String(value) : '';
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(StandardEvents.cartLinesUpdate, this.onCartUpdate);
    window.addEventListener('pageshow', this.onPageShow);
    this.ensureCartBubbleIsCorrect();
    this.syncCountFromCart();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(StandardEvents.cartLinesUpdate, this.onCartUpdate);
    window.removeEventListener('pageshow', this.onPageShow);
  }

  /**
   * Handles the page show event when the page is restored from cache.
   * @param {PageTransitionEvent} event - The page show event.
   */
  onPageShow = (event) => {
    if (event.persisted) this.ensureCartBubbleIsCorrect();
    this.syncCountFromCart();
  };

  /** Read authoritative cart quantity after navigation and on initial load. */
  syncCountFromCart = async () => {
    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, { credentials: 'same-origin', cache: 'no-store' });
      if (!response.ok || !this.isConnected) return;
      const cart = await response.json();
      if (Number.isInteger(cart.item_count) && this.isConnected) {
        this.renderCartBubble(cart.item_count, false);
      }
    } catch (error) {
      console.warn('[cart-icon] Unable to synchronize cart count:', error);
    }
  };

  /**
   * Handles the cart update event.
   * @param {CartLinesUpdateEvent} event - The cart update event.
   */
  onCartUpdate = (event) => {
    event.promise
      ?.then(({ cart, detail }) => {
        const itemCount = cart?.totalQuantity ?? detail?.itemCount ?? 0;

        this.renderCartBubble(itemCount);
        if (event.action === 'add' && !detail?.didError) {
          // Confirmation modal and fly-to-cart are handled by sth-cart-assistant.js.
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-icon] Event promise rejected:', error);
      });
  };

  /** Show feedback only after the server confirms the item was added. */
  showAddedConfirmation = (event) => {
    const source = event.target instanceof Element ? event.target : null;
    const button = source?.querySelector('add-to-cart-component button, button[type="submit"]') || source?.closest('product-form-component')?.querySelector('button[type="submit"]');
    const destination = this.querySelector('.header-actions__cart-icon') || this;
    const imageUrl = source?.querySelector('add-to-cart-component')?.dataset.productVariantMedia;
    if (button && imageUrl && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      const from = button.getBoundingClientRect();
      const to = destination.getBoundingClientRect();
      const flyer = document.createElement('img');
      flyer.src = imageUrl;
      flyer.alt = '';
      flyer.setAttribute('aria-hidden', 'true');
      Object.assign(flyer.style, {position:'fixed',left:from.left+'px',top:from.top+'px',width:'48px',height:'48px',objectFit:'cover',borderRadius:'8px',zIndex:'2147483640',pointerEvents:'none'});
      document.body.appendChild(flyer);
      const animation = flyer.animate([{opacity:1,transform:'translate(0,0) scale(1)'},{opacity:0.3,transform:'translate('+(to.left-from.left)+'px,'+(to.top-from.top)+'px) scale(.3)'}],{duration:650,easing:'ease-in-out'});
      animation.finished.catch(()=>{}).finally(()=>flyer.remove());
    }
    document.querySelector('[data-sth-cart-confirmation]')?.remove();
    const toast = document.createElement('div');
    toast.dataset.sthCartConfirmation = 'true';
    toast.setAttribute('role','status');
    toast.setAttribute('aria-live','polite');
    toast.textContent = '✓ Added to bag';
    Object.assign(toast.style,{position:'fixed',right:'16px',bottom:'24px',zIndex:'2147483640',background:'#202020',color:'#fff',padding:'12px 18px',borderRadius:'10px',boxShadow:'0 4px 20px #0004',fontSize:'14px',fontWeight:'600',pointerEvents:'none'});
    document.body.appendChild(toast);
    setTimeout(()=>toast.remove(),3000);
  };

  /**
   * Renders the cart bubble.
   * @param {number} itemCount - The absolute number of items in the cart.
   * @param {boolean} [animate=true] - Whether to animate the bubble.
   */
  renderCartBubble = async (itemCount, animate = true) => {
    this.refs.cartBubbleCount.classList.toggle('hidden', itemCount === 0);
    this.refs.cartBubble.classList.toggle('visually-hidden', itemCount === 0);

    this.currentCartCount = itemCount;

    this.classList.toggle('header-actions__cart-icon--has-cart', itemCount > 0);

    sessionStorage.setItem(
      'cart-count',
      JSON.stringify({
        value: String(this.currentCartCount),
        timestamp: Date.now(),
      })
    );

    if (!animate || itemCount === 0) return;

    // Ensure element is visible before starting animation
    // Use requestAnimationFrame to ensure the browser sees the state change
    await new Promise((resolve) => requestAnimationFrame(resolve));

    this.refs.cartBubble.classList.add('cart-bubble--animating');
    await onAnimationEnd(this.refs.cartBubbleText);

    this.refs.cartBubble.classList.remove('cart-bubble--animating');
  };

  /**
   * Checks if the cart count is correct.
   */
  ensureCartBubbleIsCorrect = () => {
    // Ensure refs are available
    if (!this.refs.cartBubbleCount) return;

    const sessionStorageCount = sessionStorage.getItem('cart-count');

    // If no session storage data, nothing to check
    if (sessionStorageCount === null) return;

    const visibleCount = this.refs.cartBubbleCount.textContent;

    try {
      const { value, timestamp } = JSON.parse(sessionStorageCount);

      // Check if the stored count matches what's visible
      if (value === visibleCount) return;

      // Only update if timestamp is recent (within 10 seconds)
      if (Date.now() - timestamp < 10000) {
        const count = parseInt(value, 10);

        if (count >= 0) {
          this.renderCartBubble(count, false);
        }
      }
    } catch (_) {
      // no-op
    }
  };
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}
